/**
 * InquiryCard component
 * 展示单个 Inquiry 任务行，用于 Inquiries 列表页（表格行样式）
 * 设计参考 Figma: node-id=9044-32240
 */

import { FC, useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button } from '@leadsnavi/ui-kit';
import type { IApiInquiryStats } from '@/services/swagger/easourcing/models';
import ProgressRing from '@/common/base/progress-ring';

/** Figma Badge 颜色 */
function getBadgeStyle(status: number): { bg: string; dotAndText: string } {
  switch (status) {
    case 1: // Paused
      return { bg: '#FFE2E2', dotAndText: '#991B1B' };
    case 2: // Completed
      return { bg: '#D1FAE5', dotAndText: '#065F46' };
    default: // Running
      return { bg: '#FEF3C7', dotAndText: '#92400E' };
  }
}

function getStatusText(status: number): string {
  switch (status) {
    case 1:
      return 'Paused';
    case 2:
      return 'Completed';
    default:
      return 'Running';
  }
}

export interface InquiryCardProps {
  inquiry: IApiInquiryStats;
  onViewDetails: (inquiryId: string) => void;
  onPauseResume?: (inquiry: IApiInquiryStats) => void;
  pauseResumeLoading?: boolean;
  className?: string;
}
export type InqiryProgressInfo = Pick<
  IApiInquiryStats,
  'companyTotalCount' | 'repliedCompanyCount' | 'contactedCompanyCount' | 'computationStatus'
>;
export const calcInqiryProgress = (inquiry: InqiryProgressInfo | undefined | null) => {
  if (!inquiry || !inquiry.companyTotalCount) return 0;
  // 任务准备中，进度为 0
  if (isInComputation(inquiry)) return 0;
  return Math.round(((inquiry.repliedCompanyCount + inquiry.contactedCompanyCount) / inquiry.companyTotalCount) * 100);
};
/**
 * 判断 Inquiry 任务是否准备完成,如果 Inquiry 不存在，则默认 true 表示准备中
 * @param inquiry
 * @returns
 */
export const isInComputation = (inquiry: Pick<IApiInquiryStats, 'computationStatus'> | undefined | null) => {
  if (inquiry) {
    return inquiry.computationStatus === 0;
  }
  return true;
};
export const computationText = 'In computation...';

/**
 * 计算 Inquiry 任务进度
 * @param inquiry
 * @returns
 */
export const useInqiryProgress = (inquiry: InqiryProgressInfo | undefined | null) => {
  return useMemo(() => {
    return calcInqiryProgress(inquiry);
  }, [inquiry]);
};

export const ComputingText = () => {
  return (
    <span className="text-[color:var(--500-fill--,#737373)]  text-xs font-normal leading-[150%] tracking-[0.18px] w-max">
      {computationText}
    </span>
  );
};

/**
 * InquiryCard - 表格行样式
 * 列布局: Title(w-[40%]) | Status(w-[15%]) | Progress(w-[15%]) | Replies(w-[15%]) | Action(w-[15%])
 * 与 InquiriesPage 中的表头列宽保持一致
 */
export const InquiryCard: FC<InquiryCardProps> = ({ inquiry, onViewDetails, className = '' }) => {
  const badge = getBadgeStyle(inquiry.status);
  const statusText = getStatusText(inquiry.status);

  return (
    <div className={`flex items-center border-t border-[#E5E5E5] bg-white px-3 h-16 ${className}`}>
      {/* Title 列 */}
      <div className="w-[40%] min-w-0 pr-4">
        <h3 className="overflow-hidden text-ellipsis whitespace-nowrap text-[14px] font-medium leading-[1.5] text-[#262626]">
          {inquiry.inquiryName || 'Untitled Inquiry'}
        </h3>
      </div>

      {/* Status 列 */}
      <div className="w-[15%] shrink-0 flex items-center">
        <span
          className="inline-flex shrink-0 items-center gap-1 rounded-[8px] px-2 text-[12px] font-medium leading-[1.333]"
          style={{
            backgroundColor: badge.bg,
            color: badge.dotAndText,
            height: 24,
            paddingTop: 2,
            paddingBottom: 2,
          }}
        >
          <span
            className="shrink-0 rounded-full"
            style={{
              width: 6,
              height: 6,
              backgroundColor: badge.dotAndText,
            }}
          />
          {statusText}
        </span>
      </div>

      {/* Contacted 列 — 进度百分比 */}
      <div className="w-[15%] shrink-0 flex items-center gap-1">
        <ProgressRing percent={calcInqiryProgress(inquiry)} size={20} color="#10B981" />
        <span className="text-[14px] font-normal leading-[1.5] tracking-[0.005em] text-[#262626]">
          {calcInqiryProgress(inquiry)}%
        </span>
      </div>

      {/* Replies 列 */}
      <div className="w-[15%] shrink-0 flex items-center gap-1">
        <span className="text-[16px] font-medium leading-[1.5] text-[#262626]">{inquiry.contactCount ?? 0}</span>
        {inquiry.unreadReplyCount > 0 && (
          // <span className="text-[12px] font-normal leading-[1.5] tracking-[0.015em] text-[#EF4444]">
          //   +{inquiry.unreadReplyCount}
          // </span>
          <></>
        )}
      </div>

      {/* View Details 按钮 */}
      <div className="w-[15%] shrink-0 flex items-center justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onViewDetails(inquiry.inquiryId)}
          className="inline-flex shrink-0 items-center gap-[6px] rounded-[8px] border border-[#E5E5E5] px-3 py-0 text-[12px] font-medium leading-[1.333] hover:opacity-90"
        >
          <span>View Details</span>
          <ChevronRight size={16} style={{ color: '#262626' }} />
        </Button>
      </div>
    </div>
  );
};
